import {
    saveDirHandle,
    loadDirHandle,
    clearDirHandle,
    recordWritten,
    clearWritten
} from "./idb.ts";

/**
 * Disk access via the File System Access API (ARCHITECTURE.md §7).
 *
 * The directory is picked once. The handle is persisted in IndexedDB, so a
 * later session re-grants permission from a user gesture rather than making the
 * user find the folder again.
 */

export class PermissionDeniedError extends Error {
    constructor() {
        super("Write permission was denied for the chosen folder");
        this.name = "PermissionDeniedError";
    }
}

export class PickerCancelledError extends Error {
    constructor() {
        super("Folder selection was cancelled");
        this.name = "PickerCancelledError";
    }
}

/**
 * A write to the destination folder failed for any reason — full disk,
 * permission lost mid-run, the folder moved or was deleted, some other I/O
 * error.
 *
 * Deliberately does not claim to know *which* of those it was: browsers do
 * not expose a reliable, consistent error name for "disk full" the way they
 * do for e.g. permission or abort, so guessing a specific cause here would be
 * asserting something this layer cannot actually verify. `cause` carries the
 * original error for the log (§14) and for anyone who wants the technical
 * detail; the user-facing message names the plausible causes without
 * pretending to have distinguished between them.
 */
export class WriteFailedError extends Error {
    constructor(path: string, cause: unknown) {
        super(`Failed to write ${path}`, { cause });
        this.name = "WriteFailedError";
    }
}

function isAbort(err: unknown): boolean {
    return err instanceof DOMException && err.name === "AbortError";
}

/** Opens the directory picker. Must be called from a user gesture. */
export async function pickDirectory(): Promise<FileSystemDirectoryHandle> {
    let handle: FileSystemDirectoryHandle;
    try {
        handle = await window.showDirectoryPicker({
            id: "o365-exporter-destination",
            mode: "readwrite",
            startIn: "documents"
        });
    } catch (err) {
        if (isAbort(err)) {
            throw new PickerCancelledError();
        }
        throw err;
    }

    await saveDirHandle(handle);

    // The written-bytes tally describes one destination folder, so it resets
    // when a different one is chosen.
    await clearWritten();

    return handle;
}

/**
 * Whether we still hold write permission on a persisted handle. Returns
 * "prompt" when the handle is valid but needs a gesture to re-grant.
 */
export async function permissionState(
    handle: FileSystemDirectoryHandle
): Promise<PermissionState> {
    return handle.queryPermission({ mode: "readwrite" });
}

/** Re-requests write permission. Must be called from a user gesture. */
export async function requestPermission(
    handle: FileSystemDirectoryHandle
): Promise<void> {
    const state = await handle.requestPermission({ mode: "readwrite" });
    if (state !== "granted") {
        throw new PermissionDeniedError();
    }
}

export interface RestoredDirectory {
    handle: FileSystemDirectoryHandle;
    /** "granted" is ready to use; "prompt" needs requestPermission() from a gesture. */
    permission: PermissionState;
}

/**
 * Loads the previously chosen directory, if there is one. Does not prompt —
 * the caller decides when to ask, since prompting needs a user gesture.
 */
export async function restoreDirectory(): Promise<RestoredDirectory | null> {
    const handle = await loadDirHandle();
    if (!handle) {
        return null;
    }

    try {
        return { handle, permission: await permissionState(handle) };
    } catch {
        // Handle no longer resolvable — the folder was deleted, or the profile
        // was moved. Drop it so the user is asked cleanly rather than seeing an
        // error against a folder that is gone.
        await clearDirHandle();
        return null;
    }
}

export async function forgetDirectory(): Promise<void> {
    await clearDirHandle();
}

/**
 * Reopens the OS folder picker already pointed at `handle`.
 *
 * The closest a web page can get to "open this folder". The File System Access
 * API exposes only `handle.name` — never the absolute path — and no browser
 * offers a way to launch Explorer or Finder. What this does give the user is
 * the system dialog showing the folder in place, with the real path visible in
 * its address bar, which they can read and then cancel.
 *
 * Returns true if they picked a folder, false if they dismissed the dialog.
 */
export async function revealDirectory(handle: FileSystemDirectoryHandle): Promise<boolean> {
    try {
        await window.showDirectoryPicker({
            id: "o365-exporter-destination",
            mode: "readwrite",
            startIn: handle
        });
        return true;
    } catch (err) {
        if (isAbort(err)) {
            return false;
        }
        throw err;
    }
}

/**
 * Characters Windows rejects in a filename, plus every control character
 * (Unicode Cc: C0, DEL and C1 — Chromium rejects all three ranges).
 * Spaces and hyphens are deliberately absent — the §7 output tree relies on
 * both (e.g. `2024-03-14_143022_<hash>.eml`).
 */
const ILLEGAL_FILENAME_CHARS = /[<>:"/\\|?*\x00-\x1f\x7f-\x9f]/g;

/**
 * Invisible code points Chromium rejects anywhere in a name
 * (base::i18n::IsFilenameLegal): format characters (Unicode Cf) and
 * noncharacters. Cf includes the zero-width joiner that glues emoji sequences
 * together (🤷‍♂️, 👨‍💻, 🏳️‍🌈), LRM/RLM and other bidi marks, zero-width
 * spaces, the BOM and the soft hyphen — all routine in chat topics and display
 * names pasted from elsewhere. Removed rather than replaced: they render as
 * nothing, so an underscore would be their only visible trace.
 */
const INVISIBLE_CHARS = /[\p{Cf}\p{Noncharacter_Code_Point}]/gu;

/** Reserved on Windows even when given an extension. */
const RESERVED_NAMES = /^(con|prn|aux|nul|com\d|lpt\d|clock\$)(\.|$)/i;

/** Further names Chromium's IsReservedNameOnWindows rejects, only exactly as written. */
const RESERVED_EXACT_NAMES = /^(conin\$|conout\$|desktop\.ini|thumbs\.db)$/i;

/**
 * Chromium on Windows rejects a name containing "~" that has the VFAT 8.3
 * short-name shape (up to 8 characters, optionally "." plus up to 3), since it
 * could alias another file's short name (CVE-2014-9390). Like Chromium, a
 * single leading dot is ignored. Applied on every platform so the same export
 * tree stays writable from Windows.
 */
function couldBeShortName(name: string): boolean {
    const s = name.startsWith(".") ? name.slice(1) : name;
    if (s.length > 12 || !s.includes("~") || /[\p{White_Space}"\/\]:+|<>=;?,*]/u.test(s)) {
        return false;
    }
    const dot = s.indexOf(".");
    if (dot === -1) {
        return s.length <= 8;
    }
    return dot === s.lastIndexOf(".") && dot > 0 && dot <= 8 && dot + 4 >= s.length;
}

/**
 * Makes a path segment safe for the filesystem. Segments come from Graph —
 * folder names, mailbox addresses, chat topics — so they can contain anything,
 * including characters Windows rejects outright and emoji.
 *
 * The rules mirror what Chromium checks before the File System Access API will
 * create a name (FileSystemAccessManagerImpl::IsSafePathComponent); anything
 * it rejects surfaces as a TypeError "Name is not allowed" from
 * getDirectoryHandle / getFileHandle. Its extension checks (.lnk, .url, .scf,
 * {CLSID}, dangerous download types) are not character problems and are
 * handled at lookup time instead — see withNameFallback().
 *
 * `maxLength` truncates by UTF-16 length but never leaves a dangling half of a
 * surrogate pair: a name cut mid-emoji ends in a lone high surrogate, which the
 * File System Access API rejects outright ("Name is not allowed"). That, plus
 * any trailing zero-width joiner / variation selector the cut exposes, is
 * trimmed so the result is always a writable name.
 *
 * Passing `maxLength` also marks the result as a label callers suffix with
 * `_<hash>` (chat, calendar, task list and OneNote keys). Rules about the name
 * as a whole — its last character, the Windows short-name shape, the
 * exact-match reserved names — apply only without it: in a label that last
 * character ends up mid-name, and changing it would rename folders that
 * already export fine (keys are recomputed on every run, so resume would
 * orphan the old folder). fsa.ts re-sanitizes every segment without
 * `maxLength` before touching the disk, so the full name always gets them.
 */
export function sanitizeSegment(name: string, maxLength?: number): string {
    let cleaned = name
        .replace(ILLEGAL_FILENAME_CHARS, "_")
        // Drop any *unpaired* surrogate — invalid on its own and unwritable.
        .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])/g, "")
        .replace(/(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "")
        .trim()
        // Windows silently drops trailing dots and spaces. Do it explicitly so
        // the name on disk matches what we recorded in IndexedDB.
        .replace(/[. ]+$/, "");

    if (maxLength !== undefined && cleaned.length > maxLength) {
        let cut = cleaned.slice(0, maxLength);
        if (/[\uD800-\uDBFF]$/.test(cut)) {
            cut = cut.slice(0, -1); // the cut split a surrogate pair — drop its dangling half
        }
        // Trim a trailing zero-width joiner / variation selector / dot / space
        // the cut may have exposed.
        cleaned = cut.replace(/[\u200D\uFE0F. ]+$/, "");
    }

    // Only after the cut: removing these first would shift which characters
    // fall inside it, renaming long-titled chats that already export fine.
    cleaned = cleaned.replace(INVISIBLE_CHARS, "");

    // Chromium allows one leading dot (names like .git) but rejects
    // whitespace, a dot or a tilde right after it — and whitespace or a tilde
    // at the very start. A lone leading dot is kept (those names were always
    // accepted); any other leading run of these characters is dropped.
    if (!/^\.[^.~\p{White_Space}]/u.test(cleaned)) {
        cleaned = cleaned.replace(/^[.~\p{White_Space}]+/u, "");
    }

    if (maxLength === undefined) {
        // Whole-name rules — see the note on `maxLength` above.
        cleaned = cleaned.replace(/[.~\p{White_Space}]+$/u, "");
        if (couldBeShortName(cleaned)) {
            cleaned = cleaned.replace(/~/g, "_");
        }
        if (RESERVED_EXACT_NAMES.test(cleaned)) {
            cleaned = `_${cleaned}`;
        }
    }

    // Prefixed rather than replaced, to keep the name recognisable.
    if (RESERVED_NAMES.test(cleaned)) {
        return `_${cleaned}`;
    }
    return cleaned === "" ? "_" : cleaned;
}

/**
 * The name a file or folder is stored under when Chromium refuses the
 * sanitized name itself: "Shortcut.lnk" becomes "Shortcut.lnk_" — the usual
 * defanging convention. Renaming it back restores the original.
 */
export function blockedNameFallback(name: string): string {
    return `${name}_`;
}

/**
 * Chromium also refuses a name for its *extension* alone, whatever its
 * characters: .lnk, .url and .scf (shell files that can run code or read
 * arbitrary files), a {CLSID} extension, and any type Safe Browsing marks as
 * dangerous on the platform — on Windows today .cfg, .dll, .ini and
 * .manifest, all routine in a synced OneDrive (Desktop shortcuts, config
 * files). That list is updated by Chrome at runtime, so rather than mirror
 * it, a lookup rejected with "Name is not allowed" (always a TypeError, per
 * FileSystemAccessStatus::kInvalidArgument) is retried once under
 * blockedNameFallback(). Every lookup in this file goes through here, so
 * reads find exactly what writes produced.
 */
async function withNameFallback<T>(name: string, open: (name: string) => Promise<T>): Promise<T> {
    try {
        return await open(name);
    } catch (err) {
        if (!(err instanceof TypeError)) {
            throw err;
        }
        return open(blockedNameFallback(name));
    }
}

/** Walks (creating as needed) to a subdirectory. Path segments are sanitized. */
export async function ensureDirectory(
    root: FileSystemDirectoryHandle,
    segments: readonly string[]
): Promise<FileSystemDirectoryHandle> {
    let dir = root;
    for (const segment of segments) {
        const parent = dir;
        dir = await withNameFallback(sanitizeSegment(segment), (name) =>
            parent.getDirectoryHandle(name, { create: true })
        );
    }
    return dir;
}

/**
 * Walks to a subdirectory without creating it. Returns null if any segment
 * along the way does not exist yet — the caller is reading back files an
 * earlier pass may not have written, not authoring new ones.
 */
export async function tryGetDirectory(
    root: FileSystemDirectoryHandle,
    segments: readonly string[]
): Promise<FileSystemDirectoryHandle | null> {
    let dir = root;
    for (const segment of segments) {
        const parent = dir;
        try {
            dir = await withNameFallback(sanitizeSegment(segment), (name) => parent.getDirectoryHandle(name));
        } catch (err) {
            if (err instanceof DOMException && err.name === "NotFoundError") {
                return null;
            }
            throw err;
        }
    }
    return dir;
}

/** Whether a file at `path` (forward-slash) exists under `root`. */
export async function fileExists(root: FileSystemDirectoryHandle, path: string): Promise<boolean> {
    const segments = path.split("/").filter((s) => s.length > 0);
    const filename = segments.pop();
    if (!filename) {
        return false;
    }
    const dir = await tryGetDirectory(root, segments);
    if (!dir) {
        return false;
    }
    try {
        await withNameFallback(sanitizeSegment(filename), (name) => dir.getFileHandle(name));
        return true;
    } catch {
        return false;
    }
}

/**
 * Reads back every file directly inside `dir` as text, in undefined order —
 * callers that need a specific order (e.g. by timestamp embedded in content)
 * sort after reading. Used for the render passes that reconstruct a page from
 * the individual records already written to disk, rather than from anything
 * held in memory (§7: resume must not depend on in-memory state surviving).
 *
 * Reads each file in full — fine for the small, self-contained records this
 * was built for (Teams' per-message JSON). Not for formats where the file can
 * be arbitrarily large and only the start matters; see readFilePrefix below.
 */
export async function* readTextFiles(
    dir: FileSystemDirectoryHandle
): AsyncGenerator<{ name: string; text: string }, void, undefined> {
    for await (const [name, handle] of dir.entries()) {
        if (handle.kind === "file") {
            const file = await handle.getFile();
            yield { name, text: await file.text() };
        }
    }
}

/** Every plain file directly inside `dir`, as handles rather than pre-read content. */
export async function* readFileHandles(
    dir: FileSystemDirectoryHandle
): AsyncGenerator<{ name: string; handle: FileSystemFileHandle }, void, undefined> {
    for await (const [name, handle] of dir.entries()) {
        if (handle.kind === "file") {
            yield { name, handle };
        }
    }
}

/**
 * Reads only the first `maxBytes` of a file as text — for a format where what
 * is needed sits at the start and the rest could be arbitrarily large (mail's
 * MIME headers vs. a multi-megabyte base64 attachment in the same .eml).
 * Rebuilding a folder index by reading every message in full would mean
 * re-reading every attachment on every rebuild for no reason.
 */
export async function readFilePrefix(
    handle: FileSystemFileHandle,
    maxBytes: number
): Promise<string> {
    const file = await handle.getFile();
    return file.slice(0, maxBytes).text();
}

export interface WalkedFile {
    /** Forward-slash path relative to the directory `walkFiles` was called on. */
    relativePath: string;
    handle: FileSystemFileHandle;
}

/**
 * Recursively walks every file under `dir`, depth-first — used by OneDrive's
 * index render pass (index-render.ts), which has no separate raw/metadata
 * directory the way Mail, Calendar, Contacts and Teams do (their per-item
 * files already live in one flat, non-mirrored directory `readFileHandles`
 * can enumerate directly). OneDrive's own output *is* the mirrored tree, so
 * reconstructing "everything on disk" — the same source-of-truth guarantee
 * every other module's render pass makes — means walking it.
 *
 * Cost is one call per directory plus one per file, same order as the tree
 * itself. Fine for a personal OneDrive; a drive in the hundreds of thousands
 * of files would make this noticeably slower, the same disclosed tradeoff
 * totalBytesWritten's own docstring already makes for the `written` store.
 */
export async function* walkFiles(
    dir: FileSystemDirectoryHandle,
    prefix = ""
): AsyncGenerator<WalkedFile, void, undefined> {
    for await (const [name, handle] of dir.entries()) {
        const relativePath = prefix ? `${prefix}/${name}` : name;
        if (handle.kind === "file") {
            yield { relativePath, handle };
        } else {
            yield* walkFiles(handle, relativePath);
        }
    }
}

export type Writable = string | BufferSource | Blob;

/**
 * Writes a file at `path` relative to `root`, creating intermediate directories.
 * `path` uses forward slashes; the final segment is the filename.
 */
export async function writeFile(
    root: FileSystemDirectoryHandle,
    path: string,
    data: Writable
): Promise<number> {
    const segments = path.split("/").filter((s) => s.length > 0);
    const filename = segments.pop();
    if (!filename) {
        throw new Error(`Invalid file path: ${path}`);
    }

    try {
        const dir = await ensureDirectory(root, segments);
        const fileHandle = await withNameFallback(sanitizeSegment(filename), (name) =>
            dir.getFileHandle(name, { create: true })
        );

        const stream = await fileHandle.createWritable();
        try {
            await stream.write(data);
        } finally {
            await stream.close();
        }

        // Size is read back from disk rather than taken from the input, so it
        // reflects what was actually written.
        const bytes = (await fileHandle.getFile()).size;

        // Recorded here rather than by callers so no write can go untracked.
        // Keyed by path, so rewriting a file replaces its size instead of
        // double-counting.
        await recordWritten(path, bytes);

        return bytes;
    } catch (err) {
        throw new WriteFailedError(path, err);
    }
}
